import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { describeAxiosError } from "@/lib/api";
import Brand from "@/components/Brand";
import { ArrowRight, EnvelopeSimple, CheckCircle } from "@phosphor-icons/react";

// SEC-P3: The backend returns a uniform response regardless of whether
// the email exists (prevents account enumeration). Frontend mirrors that
// — one confirmation card, plus a soft nudge to sign up in case the user
// doesn't actually have an account yet.
export default function ForgotPassword() {
    const [email, setEmail] = useState("");
    const [err, setErr] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null); // { message, devLink? } | null

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setLoading(true);
        try {
            const { data } = await api.post("/auth/forgot-password", { email });
            setResult({
                message: data?.message || "",
                devLink: data?.dev_reset_url || "",
            });
        } catch (ex) {
            setErr(describeAxiosError(ex));
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        setResult(null);
        setErr("");
    };

    return (
        <div className="min-h-screen w-full flex bg-paper" data-testid="forgot-password-page">
            <div className="w-full lg:w-2/3 flex items-center justify-center px-6 sm:px-12 py-12 bg-white border-r border-[#E5E5E5]">
                <div className="w-full max-w-md">
                    <div className="mb-12">
                        <Brand size="h-9" />
                    </div>

                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-3">
                        Account recovery
                    </p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mb-4">
                        Forgot your <br />
                        <span className="text-cobalt">password?</span>
                    </h1>
                    <p className="text-[#525252] mb-10 max-w-sm">
                        Enter your account email and we&apos;ll send you a link to set a new password. The link expires in 45 minutes.
                    </p>

                    {!result ? (
                        <form onSubmit={submit} className="space-y-4" data-testid="forgot-password-form">
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Email
                                </label>
                                <input
                                    data-testid="forgot-password-email-input"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    autoComplete="email"
                                    autoFocus
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-3 text-base text-ink placeholder-[#A3A3A3] focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                                />
                            </div>

                            {err && (
                                <div
                                    data-testid="forgot-password-error"
                                    className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-4 py-3"
                                >
                                    {err}
                                </div>
                            )}

                            <button
                                data-testid="forgot-password-submit-button"
                                disabled={loading}
                                type="submit"
                                className="btn-gradient w-full px-6 py-3.5 font-bold tracking-wide text-sm flex items-center justify-center gap-2"
                            >
                                {loading ? "Sending…" : "Send reset link"}{" "}
                                <ArrowRight size={16} weight="bold" />
                            </button>
                        </form>
                    ) : (
                        <div
                            data-testid="forgot-password-success"
                            className="border border-emerald-300 bg-emerald-50 px-5 py-5"
                        >
                            <div className="flex items-start gap-3">
                                <CheckCircle size={22} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                    <p className="font-bold text-emerald-900 mb-1">Check your inbox.</p>
                                    <p className="text-sm text-emerald-800">
                                        {result.message || `If ${email} is registered, we've sent a reset link. It expires in 45 minutes.`}
                                    </p>
                                </div>
                            </div>

                            {result.devLink && (
                                <div
                                    data-testid="forgot-password-dev-link"
                                    className="mt-4 pt-4 border-t border-emerald-200 text-xs text-emerald-900"
                                >
                                    <p className="font-bold uppercase tracking-wider text-[10px] text-emerald-700 mb-1">
                                        Preview mode
                                    </p>
                                    <p className="mb-2 text-emerald-800">
                                        No email provider is configured, so here&apos;s your reset link:
                                    </p>
                                    <a
                                        href={result.devLink}
                                        data-testid="forgot-password-dev-link-anchor"
                                        className="break-all underline underline-offset-2 font-mono text-[11px] text-cobalt hover:text-cobalt-dark"
                                    >
                                        {result.devLink}
                                    </a>
                                </div>
                            )}

                            <div className="mt-5 flex flex-wrap items-center gap-4 text-xs">
                                <button
                                    data-testid="forgot-password-resend-button"
                                    type="button"
                                    onClick={reset}
                                    className="font-bold text-ink underline underline-offset-2"
                                >
                                    Use a different email
                                </button>
                                <span className="text-emerald-700">·</span>
                                <Link
                                    to={`/candidate/signup?email=${encodeURIComponent(email)}`}
                                    data-testid="forgot-password-goto-signup"
                                    className="font-bold text-ink underline underline-offset-2"
                                >
                                    Don&apos;t have an account? Sign up
                                </Link>
                            </div>
                        </div>
                    )}

                    <div className="mt-10 pt-6 border-t border-[#E5E5E5] flex flex-col gap-2 text-xs text-[#525252]">
                        <Link
                            to="/candidate/login"
                            data-testid="forgot-back-candidate-login"
                            className="font-bold text-ink hover:text-cobalt"
                        >
                            ← Candidate sign in
                        </Link>
                        <Link
                            to="/login"
                            data-testid="forgot-back-recruiter-login"
                            className="font-bold text-ink hover:text-cobalt"
                        >
                            ← Recruiter sign in
                        </Link>
                    </div>
                </div>
            </div>

            <div className="hidden lg:flex w-1/3 bg-ink text-white p-12 flex-col justify-between">
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-white/40">
                    Locked out?
                </p>
                <div>
                    <p className="font-heading font-black text-6xl xl:text-7xl leading-[0.95] tracking-tighter">
                        Back in <br />
                        <span className="text-cobalt">45 minutes.</span>
                    </p>
                    <p className="mt-8 text-white/60 max-w-xs flex items-center gap-2">
                        <EnvelopeSimple size={16} weight="bold" className="text-cobalt" />
                        We&apos;ll email you a one-time link. No captchas, no phone calls.
                    </p>
                </div>
                <div className="text-xs font-bold uppercase tracking-[0.3em] text-white/40">
                    © {new Date().getFullYear()} — NaukriDaddy.com
                </div>
            </div>
        </div>
    );
}
